import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, Paper, Chip, CircularProgress, TextField, InputAdornment,
    IconButton, FormControl, InputLabel, Select, MenuItem, Divider, Grid, Card,
    CardContent, Skeleton, Tooltip, TablePagination, useTheme, alpha
} from '@mui/material';
import {
    Search, Refresh, FilterList as FilterListIcon, Sort as SortIcon,
    Clear as ClearIcon, Assignment as AssignmentIcon, SwapHoriz as SwapHorizIcon,
    Description as DescriptionIcon, Inbox as InboxIcon, MoreVert as MoreVertIcon,
    VisibilityOffRounded as VisibilityOffIcon
} from '@mui/icons-material';
import axios from 'axios';
import AdminDocumentViewDialog from './AdminDocumentViewDialog';
import AdminWarningsPanel from './AdminWarningsPanel';
import AdminSettingsPanel from './AdminSettingsPanel';

const API_BASE = 'https://tbl.zerdegroup.kz/api/v1';

// ============================================
// TYPES
// ============================================

export interface DocumentListItem {
    id: number;
    type: number;
    typeName: string;
    fromPharmacy: string;
    toPharmacy: string;
    createdAt: string;
    createdAtISO?: string;
    itemsCount: number;
    status: number;
    statusName: string;
    comment: string;
    boxCount: number;
    updateDate: string;
    username: string;
    typeMovement?: number;
    currentPoint?: string;
    totalCost?: number;
    isHidden?: boolean;
}

export interface DocumentDetails {
    id: number;
    movementId: string;
    type: number;
    typeName: string;
    status: number;
    statusName: string;
    fromPharmacy: string;
    fromPharmacyId: string;
    toPharmacy: string;
    toPharmacyId: string;
    createdAt: string;
    updateDate: string;
    checkDate1c: string;
    username: string;
    user1C: string;
    comment: string;
    isWeb: boolean;
    push: number;
    typeMovement: number;
    currentPoint: string;
    pointZid: number;
    boxCount: number;
    itemsCount: number;
    purchaseDoc: string;
    incomingNumber: string;
    incomingDate: string;
    processStatus: string;
    items: Array<{
        id: number;
        materialCode: string;
        materialName: string;
        serialNumber: string;
        serialCode: string;
        serialDate: string;
        manufacturerId: number;
        manufacturerName: string;
        quantity: number;
        quantityFact: number;
        quantityPcs: number;
        quantityFactPart: number;
        quantity1C: number | null;
        unitName: string;
        koeff: number;
        basePrice: number;
        stockPrice: number;
        lineTotal: number;
        barcode: string;
        processStatus: string;
        checkDate1c: string;
        partExists: boolean;
        updateDate: string;
        markCodes: Array<{ id: number; mark: string }>;
    }>;
    statusHistory: Array<{
        statusZid: number;
        statusName: string;
        description: string;
        recordDate: string;
        username: string;
    }>;
    boxes: Array<{
        id: number;
        boxNum: number;
        barcode: string;
        recordDate: string;
    }>;
    canRetry: boolean;
    retryStatus: number | null;
    totalCost: number;
}

interface PaginationInfo {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

interface StatsInfo {
    total: number;
    drafts: number;
    inProgress: number;
    totalErrors: number;
    transfers: number;
    returns: number;
}

type FilterType = number | 'all';
type FilterStatus = number | 'all' | 'error' | 'in_progress';
type SortBy = 'id' | 'date' | 'type' | 'status';
type SortOrder = 'asc' | 'desc';

// ============================================
// COLORS & STATUS HELPERS
// ============================================

const getStatusColor = (status: number): string => {
    switch (status) {
        case 1: return '#3b82f6';     // Новый
        case 2: return '#6366f1';     // Передан
        case 3: return '#10b981';     // Принят
        case 4: return '#10b981';     // Принят частично
        case 5: return '#ef4444';     // Отклонен
        case 6: return '#64748b';     // В пути
        case 7: return '#3b82f6';     // В работе
        case 9: return '#f59e0b';     // Черновик
        case 10: return '#10b981';    // Принят с расхождениями
        case 11: return '#ef4444';    // Ошибка остатка
        case 12: return '#f59e0b';    // Сканирование коробов
        case 13: return '#f59e0b';    // Подтвердить отправку
        case 14: return '#ef4444';    // Ошибка 1С
        case 15: return '#22c55e';    // Ожидает подтверждения
        case 16: return '#64748b';    // Завершен
        default: return '#94a3b8';
    }
};

const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    try {
        if (dateString.includes('.') && dateString.includes(',')) return dateString;
        const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/);
        if (match) {
            return `${match[3]}.${match[2]}.${match[1]}, ${match[4]}:${match[5]}:${match[6]}`;
        }
        return dateString;
    } catch { return dateString; }
};

// ============================================
// STAT CARD
// ============================================

interface StatCardProps {
    label: string;
    value: number;
    color: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
    active?: boolean;
    onClick: () => void;
    icon?: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color, active, onClick, icon }) => {
    const theme = useTheme();
    return (
        <Card
            elevation={0}
            sx={{
                bgcolor: active ? alpha(theme.palette[color].main, 0.1) : 'white',
                border: '1px solid',
                borderColor: active ? theme.palette[color].main : 'divider',
                cursor: 'pointer',
                transition: 'all 0.2s',
                position: 'relative',
                overflow: 'hidden',
                height: 100,
                '&:hover': {
                    borderColor: theme.palette[color].main,
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[2]
                }
            }}
            onClick={onClick}
        >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, position: 'relative', zIndex: 1 }}>
                <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {label}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} color={value > 0 ? 'text.primary' : 'text.disabled'} sx={{ mt: 1 }}>
                        {value}
                    </Typography>
                </Box>
            </CardContent>
            <Box sx={{
                position: 'absolute', right: -20, bottom: -20,
                color: theme.palette[color].main, opacity: active ? 0.2 : 0.1,
                transform: 'rotate(-20deg)', zIndex: 0, pointerEvents: 'none',
                '& svg': { fontSize: '120px' }
            }}>
                {icon || <DescriptionIcon />}
            </Box>
        </Card>
    );
};

// ============================================
// GRID CONFIG
// ============================================

const GRID_TEMPLATE = '60px minmax(120px, 0.5fr) 150px minmax(150px, 1.2fr) minmax(150px, 1.2fr) 100px 140px 120px 70px';
const MIN_WIDTH = 1100;

// ============================================
// MAIN COMPONENT
// ============================================

const AdminDocuments: React.FC = () => {
    const theme = useTheme();

    // Data
    const [documents, setDocuments] = useState<DocumentListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [filterType, setFilterType] = useState<FilterType>('all');
    const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchInput, setSearchInput] = useState(''); // debounced input

    // Sorting
    const [sortBy, setSortBy] = useState<SortBy>('id');
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

    // Pagination
    const [pagination, setPagination] = useState<PaginationInfo>({ page: 1, limit: 50, total: 0, totalPages: 0 });

    // Stats
    const [stats, setStats] = useState<StatsInfo>({ total: 0, drafts: 0, inProgress: 0, totalErrors: 0, transfers: 0, returns: 0 });

    // Detail dialog
    const [selectedDoc, setSelectedDoc] = useState<DocumentDetails | null>(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    // ---- Fetch documents ----
    const fetchDocuments = useCallback(async (opts?: {
        page?: number; limit?: number; type?: FilterType; status?: FilterStatus;
        search?: string; sortBy?: SortBy; sortOrder?: SortOrder;
    }) => {
        setLoading(true);
        setError(null);
        try {
            const params: any = {
                page: opts?.page ?? pagination.page,
                limit: opts?.limit ?? pagination.limit,
                sortBy: opts?.sortBy ?? sortBy,
                sortOrder: opts?.sortOrder ?? sortOrder,
            };

            const type = opts?.type ?? filterType;
            if (type !== 'all') params.type = type;

            const status = opts?.status ?? filterStatus;
            if (status !== 'all') params.status = status;

            const search = opts?.search ?? searchQuery;
            if (search) params.search = search;

            const response = await axios.get(`${API_BASE}/admin/documents`, { params });
            if (response.data.success) {
                setDocuments(response.data.documents);
                setPagination(response.data.pagination);
                setStats(response.data.stats);
            } else {
                setError(response.data.message || 'Ошибка загрузки');
            }
        } catch (err: any) {
            setError(err.message || 'Ошибка при загрузке документов');
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, filterType, filterStatus, searchQuery, sortBy, sortOrder]);

    // Initial load
    useEffect(() => {
        fetchDocuments({ page: 1 });
    }, []);

    // ---- Debounce search ----
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchInput !== searchQuery) {
                setSearchQuery(searchInput);
                fetchDocuments({ page: 1, search: searchInput });
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    // ---- Handlers ----
    const handleFilterTypeChange = (type: FilterType) => {
        setFilterType(type);
        fetchDocuments({ page: 1, type });
    };

    const handleFilterStatusChange = (status: FilterStatus) => {
        setFilterStatus(status);
        fetchDocuments({ page: 1, status });
    };

    const handleSortByChange = (newSortBy: SortBy) => {
        setSortBy(newSortBy);
        fetchDocuments({ page: 1, sortBy: newSortBy });
    };

    const handleSortOrderChange = (newSortOrder: SortOrder) => {
        setSortOrder(newSortOrder);
        fetchDocuments({ page: 1, sortOrder: newSortOrder });
    };

    const handleResetFilters = () => {
        setFilterType('all');
        setFilterStatus('all');
        setSearchInput('');
        setSearchQuery('');
        setSortBy('id');
        setSortOrder('desc');
        fetchDocuments({ page: 1, type: 'all', status: 'all', search: '', sortBy: 'id', sortOrder: 'desc' });
    };

    const handleRefresh = () => {
        fetchDocuments();
    };

    const handlePageChange = (_: unknown, newPage: number) => {
        fetchDocuments({ page: newPage + 1 }); // MUI is 0-indexed
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newLimit = parseInt(event.target.value, 10);
        fetchDocuments({ page: 1, limit: newLimit });
    };

    const handleRowClick = async (docId: number) => {
        setDetailsLoading(true);
        setDialogOpen(true);
        try {
            const response = await axios.get(`${API_BASE}/admin/documents/${docId}`);
            if (response.data.success) {
                setSelectedDoc(response.data.document);
            }
        } catch (err: any) {
            console.error('Ошибка при загрузке документа:', err);
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedDoc(null);
    };

    const handleRefreshDocument = async (docId: number) => {
        setDetailsLoading(true);
        try {
            const response = await axios.get(`${API_BASE}/admin/documents/${docId}`);
            if (response.data.success) {
                setSelectedDoc(response.data.document);
            }
        } catch (err: any) {
            console.error('Ошибка при обновлении документа:', err);
        } finally {
            setDetailsLoading(false);
        }
    };

    // ============================================
    // RENDER
    // ============================================

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

            {/* 1. Stat Cards */}
            <Box>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={4} md={2}>
                        <StatCard
                            label="Черновики"
                            value={stats.drafts}
                            color="warning"
                            active={filterStatus === 9}
                            onClick={() => handleFilterStatusChange(filterStatus === 9 ? 'all' : 9)}
                        />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <StatCard
                            label="В работе"
                            value={stats.inProgress}
                            color="info"
                            active={filterStatus === 'in_progress'}
                            onClick={() => handleFilterStatusChange(filterStatus === 'in_progress' ? 'all' : 'in_progress')}
                        />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <StatCard
                            label="Ошибки"
                            value={stats.totalErrors}
                            color="error"
                            active={filterStatus === 'error'}
                            onClick={() => handleFilterStatusChange(filterStatus === 'error' ? 'all' : 'error')}
                        />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <StatCard
                            label="Перемещения"
                            value={stats.transfers}
                            color="secondary"
                            active={filterType === 5}
                            onClick={() => handleFilterTypeChange(filterType === 5 ? 'all' : 5)}
                            icon={<SwapHorizIcon fontSize="small" />}
                        />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <StatCard
                            label="Возвраты"
                            value={stats.returns}
                            color="primary"
                            active={filterType === 4}
                            onClick={() => handleFilterTypeChange(filterType === 4 ? 'all' : 4)}
                            icon={<AssignmentIcon fontSize="small" />}
                        />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <StatCard
                            label="Всего"
                            value={stats.total}
                            color="info"
                            active={false}
                            onClick={() => handleResetFilters()}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* 2. Filters Toolbar */}
            <Box sx={{ mb: 2 }}>
                <Paper
                    elevation={0}
                    variant="outlined"
                    sx={{
                        p: 2, display: 'flex', alignItems: 'center',
                        flexWrap: 'wrap', gap: 2, bgcolor: 'background.paper', borderRadius: 2
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                        <FilterListIcon fontSize="small" />
                        <Typography variant="subtitle2" fontWeight={600}>Фильтр:</Typography>
                    </Box>

                    {/* Search */}
                    <TextField
                        size="small"
                        placeholder="Поиск по ID, аптеке, автору..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={{ minWidth: 260 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Search sx={{ color: 'text.secondary' }} /></InputAdornment>,
                        }}
                    />

                    {/* Type filter */}
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Тип документа</InputLabel>
                        <Select
                            value={filterType}
                            label="Тип документа"
                            onChange={(e) => handleFilterTypeChange(e.target.value as FilterType)}
                        >
                            <MenuItem value="all">Все типы</MenuItem>
                            <MenuItem value={4}>Возврат</MenuItem>
                            <MenuItem value={5}>Перемещение</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Status filter */}
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Статус</InputLabel>
                        <Select
                            value={filterStatus}
                            label="Статус"
                            onChange={(e) => handleFilterStatusChange(e.target.value as FilterStatus)}
                        >
                            <MenuItem value="all">Все статусы</MenuItem>
                            <Divider sx={{ my: 0.5 }} />
                            <MenuItem value={9}>Черновик</MenuItem>
                            <MenuItem value={1}>Новый</MenuItem>
                            <MenuItem value={7}>В работе</MenuItem>
                            <MenuItem value={15}>Ожидает подтверждения</MenuItem>
                            <Divider sx={{ my: 0.5 }} />
                            <MenuItem value={2}>Передан</MenuItem>
                            <MenuItem value={6}>В пути</MenuItem>
                            <MenuItem value={3}>Принят</MenuItem>
                            <MenuItem value={4}>Принят частично</MenuItem>
                            <MenuItem value={10}>Принят с расхождениями</MenuItem>
                            <MenuItem value={16}>Завершен</MenuItem>
                            <Divider sx={{ my: 0.5 }} />
                            <MenuItem value={5} sx={{ color: 'error.main' }}>Отклонен</MenuItem>
                            <MenuItem value={11} sx={{ color: 'error.main' }}>Ошибка остатка</MenuItem>
                            <MenuItem value={14} sx={{ color: 'error.main' }}>Ошибка 1С</MenuItem>
                            <Divider sx={{ my: 0.5 }} />
                            <MenuItem value="in_progress">🔵 В работе (группа)</MenuItem>
                            <MenuItem value="error">🔴 Ошибки (группа)</MenuItem>
                        </Select>
                    </FormControl>

                    <Box sx={{ flexGrow: 1 }} />

                    {/* Sort */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                        <SortIcon fontSize="small" />
                        <Typography variant="subtitle2" fontWeight={600}>Сортировка:</Typography>
                    </Box>

                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select value={sortBy} onChange={(e) => handleSortByChange(e.target.value as SortBy)} displayEmpty>
                            <MenuItem value="id">По номеру</MenuItem>
                            <MenuItem value="date">По дате</MenuItem>
                            <MenuItem value="type">По типу</MenuItem>
                            <MenuItem value="status">По статусу</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select value={sortOrder} onChange={(e) => handleSortOrderChange(e.target.value as SortOrder)} displayEmpty>
                            <MenuItem value="desc">Сначала новые</MenuItem>
                            <MenuItem value="asc">Сначала старые</MenuItem>
                        </Select>
                    </FormControl>

                    <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                    <IconButton onClick={handleResetFilters} size="small" title="Сбросить фильтры">
                        <ClearIcon fontSize="small" />
                    </IconButton>

                    <IconButton onClick={handleRefresh} disabled={loading} size="small" title="Обновить"
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Refresh fontSize="small" />
                    </IconButton>
                    <AdminSettingsPanel />
                    <AdminWarningsPanel />
                </Paper>
            </Box>

            {/* 3. Document List */}
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                <Paper elevation={0} variant="outlined" sx={{ width: '100%', border: 'none', position: 'relative' }}>
                    <Box sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { height: 6 } }}>
                        <Box sx={{ minWidth: MIN_WIDTH }}>
                            {/* Header */}
                            <Box sx={{
                                display: 'grid', gridTemplateColumns: GRID_TEMPLATE, gap: 1,
                                px: 2, py: 1.5, bgcolor: 'transparent', borderBottom: '1px solid',
                                borderColor: 'divider', alignItems: 'center'
                            }}>
                                <HeaderCell>ID</HeaderCell>
                                <HeaderCell>Тип</HeaderCell>
                                <HeaderCell>Статус</HeaderCell>
                                <HeaderCell>Отправитель</HeaderCell>
                                <HeaderCell>Получатель</HeaderCell>
                                <HeaderCell>Автор</HeaderCell>
                                <HeaderCell>Дата</HeaderCell>
                                <HeaderCell>Стоимость</HeaderCell>
                                <HeaderCell align="right">Поз.</HeaderCell>
                            </Box>

                            {/* Rows */}
                            {loading ? (
                                [...Array(10)].map((_, i) => (
                                    <Box key={i} sx={{
                                        display: 'grid', gridTemplateColumns: GRID_TEMPLATE, gap: 1,
                                        px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', minWidth: MIN_WIDTH
                                    }}>
                                        {[...Array(9)].map((_, j) => <Skeleton key={j} variant="text" width="80%" />)}
                                    </Box>
                                ))
                            ) : documents.length === 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 6, bgcolor: 'background.default' }}>
                                    <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, color: 'text.secondary' }}>
                                        <InboxIcon sx={{ fontSize: 40 }} />
                                    </Box>
                                    <Typography variant="h6" color="text.primary" gutterBottom>
                                        Документы не найдены
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 400 }}>
                                        Попробуйте изменить параметры фильтрации или сбросить фильтры
                                    </Typography>
                                </Box>
                            ) : (
                                documents.map((doc) => (
                                    <DocumentRow
                                        key={doc.id}
                                        doc={doc}
                                        onClick={() => handleRowClick(doc.id)}
                                    />
                                ))
                            )}
                        </Box>
                    </Box>

                    {/* Pagination Footer */}
                    <Box sx={{
                        px: 2, borderTop: '1px solid', borderColor: 'divider',
                        bgcolor: 'grey.50', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', bottom: 0, zIndex: 100
                    }}>
                        <Typography variant="body2" color="text.secondary">
                            Найдено: <strong>{pagination.total}</strong> документов
                        </Typography>
                        <TablePagination
                            component="div"
                            count={pagination.total}
                            page={pagination.page - 1}
                            rowsPerPage={pagination.limit}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            rowsPerPageOptions={[25, 50, 100, 200]}
                            labelRowsPerPage="На странице:"
                            labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
                        />
                    </Box>
                </Paper>
            </Box >

            {/* 4. Detail Dialog */}
            <AdminDocumentViewDialog
                open={dialogOpen}
                document={selectedDoc}
                loading={detailsLoading}
                onClose={handleCloseDialog}
                onRefreshDocument={handleRefreshDocument}
            />
        </Box >
    );
};

// ============================================
// SUB-COMPONENTS
// ============================================

const HeaderCell: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
    <Typography
        variant="subtitle2" color="text.secondary" fontWeight={600} align={align}
        sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
    >
        {children}
    </Typography>
);

interface DocumentRowProps {
    doc: DocumentListItem;
    onClick: () => void;
}

const DocumentRow: React.FC<DocumentRowProps> = React.memo(({ doc, onClick }) => {
    const theme = useTheme();
    const statusColor = getStatusColor(doc.status);
    const isHidden = doc.isHidden === true;

    return (
        <Tooltip
            title={isHidden ? 'Документ скрыт от исполнителей (автоматически отменён)' : ''}
            placement="left"
            arrow
            disableHoverListener={!isHidden}
        >
            <Box
                onClick={onClick}
                sx={{
                    display: 'grid', gridTemplateColumns: GRID_TEMPLATE, gap: 1,
                    px: 2, alignItems: 'center', height: 54,
                    borderBottom: '1px solid', borderColor: 'divider',
                    cursor: 'pointer',
                    bgcolor: isHidden ? alpha('#78716c', 0.05) : 'transparent',
                    borderLeft: isHidden ? '3px solid' : '3px solid transparent',
                    borderLeftColor: isHidden ? '#9ca3af' : 'transparent',
                    transition: 'all 0.15s ease',
                    opacity: isHidden ? 0.72 : 1,
                    '&:hover': {
                        bgcolor: isHidden
                            ? alpha('#78716c', 0.1)
                            : alpha(theme.palette.primary.main, 0.04),
                    }
                }}
            >
                {/* ID */}
                <Typography variant="body2" fontWeight={600} fontFamily="monospace" color={isHidden ? 'text.disabled' : 'text.primary'}>
                    #{doc.id}
                </Typography>

                {/* Type */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, borderRadius: '6px',
                        bgcolor: doc.type === 4
                            ? alpha(theme.palette.info.main, 0.1)
                            : alpha(theme.palette.secondary.main, 0.1),
                        color: doc.type === 4 ? theme.palette.info.main : theme.palette.secondary.main
                    }}>
                        {doc.type === 4 ? <AssignmentIcon sx={{ fontSize: 16 }} /> : <SwapHorizIcon sx={{ fontSize: 16 }} />}
                    </Box>
                    <Typography variant="body2" fontWeight={500} color={isHidden ? 'text.disabled' : 'text.primary'}>
                        {doc.type === 4 ? 'Возврат' : 'Перемещ.'}
                    </Typography>
                </Box>

                {/* Status */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Chip
                        label={doc.statusName}
                        size="small"
                        sx={{
                            height: 24, fontSize: '0.75rem', fontWeight: 500,
                            bgcolor: alpha(statusColor, 0.1),
                            color: statusColor,
                            border: '1px solid',
                            borderColor: alpha(statusColor, 0.2)
                        }}
                    />
                    {isHidden && (
                        <Chip
                            icon={<VisibilityOffIcon sx={{ fontSize: '13px !important' }} />}
                            label="Скрыт"
                            size="small"
                            sx={{
                                height: 20, fontSize: '0.7rem', fontWeight: 600,
                                bgcolor: alpha('#6b7280', 0.12),
                                color: '#6b7280',
                                border: '1px solid',
                                borderColor: alpha('#6b7280', 0.25),
                                '& .MuiChip-icon': { color: '#6b7280' }
                            }}
                        />
                    )}
                </Box>

                {/* From */}
                <Typography variant="body2" color={isHidden ? 'text.disabled' : 'text.secondary'} noWrap title={doc.fromPharmacy || ''}>
                    {doc.fromPharmacy || '-'}
                </Typography>

                {/* To */}
                <Typography variant="body2" color={isHidden ? 'text.disabled' : 'text.secondary'} noWrap title={doc.toPharmacy || ''}>
                    {doc.toPharmacy || '-'}
                </Typography>

                {/* Author */}
                <Typography variant="body2" color={isHidden ? 'text.disabled' : 'text.secondary'} noWrap title={doc.username || ''}>
                    {doc.username || '-'}
                </Typography>

                {/* Date */}
                <Typography variant="body2" color={isHidden ? 'text.disabled' : 'text.secondary'} sx={{ fontSize: '0.8125rem' }}>
                    {formatDate(doc.createdAt)}
                </Typography>

                {/* Cost */}
                <Typography variant="body2" noWrap color={isHidden ? 'text.disabled' : 'text.primary'}>
                    {doc.totalCost
                        ? doc.totalCost.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₸'
                        : '—'}
                </Typography>

                {/* Items count */}
                <Typography variant="body2" align="right" fontWeight={500} color={isHidden ? 'text.disabled' : 'text.primary'}>
                    {doc.itemsCount || 0}
                </Typography>
            </Box>
        </Tooltip>
    );
}, (prev, next) => prev.doc.id === next.doc.id && prev.doc.status === next.doc.status && prev.doc.isHidden === next.doc.isHidden);

DocumentRow.displayName = 'DocumentRow';

export default AdminDocuments;